// Orion Archive Reader (Step 4a) — single-entry forensic renderer.
//
// Doctrinal posture (see audit note):
//   - admin-only
//   - outcome-hidden (Step 4d not authorized)
//   - replay-disabled
//   - direct-entry-only (no index, no list, no traversal between entries)
//   - no prefetching, no preloading, no hover-triggered fetches
//   - no copy affordances, no exports, no telemetry
//
// This route is the only route under /_authenticated/archive at Step 4a.
// There is intentionally no /_authenticated/archive index route.

import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { readArchiveEntryFn } from "@/lib/archive/reader.functions";
import { ArchiveStateBanner } from "@/components/archive/ArchiveStateBanner";
import { EntryHeader } from "@/components/archive/EntryHeader";
import { DecisionTimeProjection } from "@/components/archive/DecisionTimeProjection";
import { ReplayPinPanel } from "@/components/archive/ReplayPinPanel";
import { RevisionLogPanel } from "@/components/archive/RevisionLogPanel";
import { SelfCritiquePanel } from "@/components/archive/SelfCritiquePanel";
import { AntiMythologyLintPanel } from "@/components/archive/AntiMythologyLintPanel";
import { CaptureMissingNotice } from "@/components/archive/CaptureMissingNotice";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const Route = createFileRoute("/_authenticated/archive/$entryId")({
  ssr: false,
  // No preloading of any kind. Direct-entry-only inspection posture.
  preload: false,
  staleTime: 0,
  beforeLoad: async ({ params }) => {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess.session?.user.id;
    if (!userId) {
      throw redirect({ to: "/auth", search: { tab: "login" } });
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (error || !data) {
      throw redirect({ to: "/dashboard" });
    }
    const normalized = (params.entryId ?? "").trim().toLowerCase();
    if (!UUID_RE.test(normalized)) {
      throw notFound();
    }
  },
  component: ArchiveEntryView,
});

function ArchiveEntryView() {
  const { entryId } = Route.useParams();
  const fetchEntry = useServerFn(readArchiveEntryFn);
  const normalized = entryId.trim().toLowerCase();

  const { data, isLoading, error } = useQuery({
    queryKey: ["orion-archive-entry", normalized],
    queryFn: () => fetchEntry({ data: { entry_id: normalized } }),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-8">
      <div className="space-y-4">
        <ArchiveStateBanner />

        {isLoading ? (
          <div className="border border-border/60 bg-card/40 px-4 py-3 font-mono text-xs text-muted-foreground">
            loading…
          </div>
        ) : null}

        {error ? (
          <div className="border border-border/60 bg-card/40 px-4 py-3 font-mono text-xs text-destructive">
            {error instanceof Error ? error.message : String(error)}
          </div>
        ) : null}

        {data ? (
          <>
            <EntryHeader entry={data.entry} />

            {data.capture.state === "capture_missing" ? (
              <CaptureMissingNotice capture_id={data.capture.capture_id} />
            ) : (
              <>
                <DecisionTimeProjection
                  source_event_kind={data.capture.source_event_kind}
                  source_event_id={data.capture.source_event_id}
                  decided_at={data.capture.decided_at}
                  decision_time_projection={
                    data.capture.decision_time_projection
                  }
                />
                <ReplayPinPanel pin={data.capture.replay_pin} />
              </>
            )}

            <SelfCritiquePanel text={data.entry.self_critique} />
            <AntiMythologyLintPanel
              lint={data.entry.anti_mythology_lint_notes}
              limitations={data.entry.sole_admin_review_limitations}
            />
            <RevisionLogPanel
              entry_id={data.entry.id}
              revisions={data.revisions}
            />
          </>
        ) : null}
      </div>
    </main>
  );
}
